import { useEffect, useState, useRef } from 'react';
import {
  createCustomAlert,
  fromStateToRequest,
  initialState,
  disableSaveButton
} from '../utils/utils';
import Panel from './Panels/Panel';
import PanelPush from './Panels/PanelPush';
import PanelBanner from './Panels/PanelBanner';
import PreviewApp from './Previews/PreviewApp';
import PreviewDesktop from './Previews/PreviewDesktop';
import PreviewPush from './Previews/PreviewPush';
import PreviewPushExpanded from './Previews/PreviewPushExpanded';
import Spinner from './Spinner';
import SplitPanelRouter from './SplitPanelRouter';
import { DEFAULT_ROUTES_BY_TEMPLATE, TEMPLATE_TYPE } from '../utils/constants';
import RetrieveVersionModal from './RetrieveVersionModal/RetrieveVersionModal';
import BannerQueueModal from './BannerQueueModal';
import PanelBannerQueueManager from './Panels/PanelBannerQueueManager';
import PanelTtlQueueManager from './Panels/PanelTtlQueueManager/PanelTtlQueueManager';
import PreviewQueueBanner from './Previews/PreviewQueueBanner';
import PreviewBannerWrapper from './Previews/PreviewBanner/PreviewBannerWrapper';
import PreviewNotifica from './Previews/PreviewNotifica/PreviewNotifica';
import Footer from './Footer';

function confirmPrompt() {
  return window.confirm(`Una campagna non può essere attivata per TTL e PUSH contemporaneamente.
Vuoi proseguire? Perderai i dati inseriti fin'ora`);
}

const Main = ({ api, state, setState, ...props }) => {

  const { getMarketingAutomationInfo, getTemplate } = api;
  const { info } = state;
  const hasInfo = info ? Object.keys(info).length > 0 : false;
  const templateType = state?.values?.templateType || TEMPLATE_TYPE.TTL;
  const selectedMainActiveRoute = DEFAULT_ROUTES_BY_TEMPLATE[templateType].mainActiveRoute;
  const selectedSubActiveRoute = DEFAULT_ROUTES_BY_TEMPLATE[templateType].subActiveRoute;
  const [currentPath, setCurrentPath] = useState('/');
  const [retrieveVersionModalIsVisible, setRetrieveVersionModalIsVisible] = useState(false);
  const [bannerQueueModalIsVisible, setBannerQueueModalIsVisible] = useState(false);
  const splitPanelRouterRef = useRef();

  useEffect(() => {
    //se apro un promoBanner già presente devo spostarmi nel form del banner
    if (state && state.values && state.values.templateType &&
          state.values.templateType === TEMPLATE_TYPE.BANNER &&
            state.slots && state.slots.flagNewTemplate === false ) {
      navigate('/banner/create');
    }
  }, [state?.values?.templateType, state?.slots?.flagNewTemplate]);

  useEffect(() => {
    if(currentPath !== '/') navigate('/');
    getMarketingAutomationInfo();
  }, [getMarketingAutomationInfo]);

  useEffect(() => {
    if (hasInfo) {
      getTemplate();
    }
  }, [getTemplate, hasInfo]);

  /**
   * Fetches campaign versions for a specific template type from the API.
   *
   * @async
   * @function fetchCampaignVersions
   * @param {string} templateType - The type of template to retrieve versions for
   * @returns {Promise<Array>} A promise that resolves to an array of campaign versions, or an empty array if the request fails or returns invalid data
   *
   * @description
   * This function calls the API to retrieve banner versions based on the provided template type.
   * It handles the modal visibility state and validates the response data structure.
   * If the response contains a valid versions array, it returns that array; otherwise, it returns an empty array.
   */
  const fetchCampaignVersions = async (templateType) => {
    const res = await api.getRunningCampaignVersions(setRetrieveVersionModalIsVisible, templateType);
    if (res && res.data && Array.isArray(res.data.versions)) {
      return res.data.versions;
    }
    return [];
  };

  /**
   * Handles the change of banner version selection by updating the state with the new version ID.
   * @param {string|number} versionId - The ID of the selected banner version
   */
  const handleCampaignVersionChange = (versionId, templateType) => {
    setState((prev) => ({
      ...prev,
      slots: {
        ...prev.slots,
        selectedCampaign: {
          ...prev.slots.selectedCampaign,
          version: versionId,
          templateType: templateType
        }
      }
    }));
  };

  const handleCloseRetrieveVersionModal = () => {
    setRetrieveVersionModalIsVisible(false);
    setState((prev) => ({
      ...prev,
      slots: {
        ...prev.slots,
        selectedCampaign: {}
      }
    }));
  }

  /**
   * Handles the confirmation of a banner version selection.
   * Retrieves the running configuration for the specified version and navigates to the queue manager if successful.
   *
   * @async
   * @function handleBannerConfirm
   * @param {string|number} versionId - The ID of the version to confirm and retrieve configuration for
   * @returns {Promise<void>} A promise that resolves when the operation is complete
   * @description This function fetches the running configuration using the provided version ID and template type,
   * then navigates to the banner queue manager page if the request is successful (status 200).
   * Finally, it hides the retrieve version modal regardless of the response status.
   */
  const handleBannerConfirm = async (versionId) => {
    const response = await api.getRunningConfiguration(versionId, templateType);

    if (response && response.status === 200) {
      navigate('/banner/queueManager');
    }

    setRetrieveVersionModalIsVisible(false);
  };

  /**
   * Handles the TTL confirmation process by retrieving the running configuration
   * and navigating to the queue manager if successful.
   *
   * @async
   * @function handleTTLConfirm
   * @param {string|number} versionId - The ID of the version to retrieve configuration for
   * @returns {Promise<void>} A promise that resolves when the operation is complete
   * @throws {Error} May throw an error if the API call fails
   */
  const handleTTLConfirm = async (versionId) => {
    const response = await api.getRunningConfiguration(versionId, templateType);

    if (response && response.status === 200) {
      navigate('/ttl/queueManager');
    }

    setRetrieveVersionModalIsVisible(false);
  };

  /**
   * Builds configuration properties for the retrieve version component based on template type.
   *
   * @function buildRetrieveVersionProps
   * @returns {Object|null} Configuration object containing fetch function, event handlers, and UI labels, or null for unsupported template types
   * @returns {Function} returns.fetchCampaignVersions - Function to fetch campaign versions
   * @returns {Function} returns.onVersionChange - Handler for version selection changes
   * @returns {Function} returns.onConfirm - Handler for version confirmation
   * @returns {string} returns.title - Display title for the version selection interface
   * @returns {string} returns.versionsLabel - Label for the versions list
   * @returns {string} returns.placeholder - Placeholder text for the selection input
   *
   * @description
   * Returns different configuration objects based on the current templateType:
   * - BANNER: Configures for journey version selection
   * - TTL: Configures for TTL campaign version selection
   * - default: Returns null for unsupported types
   */
  const buildRetrieveVersionProps = () => {
    switch (templateType) {
      case TEMPLATE_TYPE.BANNER:
        return {
          fetchCampaignVersions,
          onVersionChange: handleCampaignVersionChange,
          onConfirm: handleBannerConfirm,
          title: 'Scegli la versione del tuo journey',
          versionsLabel: 'Lista versioni',
          placeholder: 'Seleziona un ID'
        };

      case TEMPLATE_TYPE.TTL:
        return {
          fetchCampaignVersions,
          onVersionChange: handleCampaignVersionChange,
          onConfirm: handleTTLConfirm,
          title: 'Scegli la versione della campagna TTL',
          versionsLabel: 'Lista versioni TTL',
          placeholder: 'Seleziona una campagna'
        };
      default:
        return null;
    }
  };

  const retrieveVersionProps = buildRetrieveVersionProps();

  const setResetSubroutes = () => {
    //permette di resettare le tab della preview
    //viene chiamata dal componente SplitPanelRouter
    if (splitPanelRouterRef.current) {
      splitPanelRouterRef.current.resetSubRoutes();
    }
  };

  const navigate = (path) => {
    setCurrentPath(path);
  };

  function handleChangeTab(templateType) {
    if (state.values.templateType !== templateType) {
      let isDisabled = Object.entries(state.values).some(([key, value]) => key !== 'templateType' && value);
      if (isDisabled) {
        if (confirmPrompt()) {
          setState(prev => ({ ...prev, values: {...initialState, templateType: templateType} }));
          return true;
        }
      } else {
        setState(prev => ({ ...prev, values: {...prev.values, templateType: templateType} }));
        return true;
      }
      return false;
    }
  }

  const handleBackToBannerForm = () => {
    navigate('/banner/create');
    setResetSubroutes();
  }

  const clearSelectedTtl = (prev) => {
    if (!prev?.slots?.selectedTtl) {
      return prev;
    }

    const { selectedTtl, ...slots } = prev.slots || {};
    return {
      ...prev,
      slots
    };
  };

  const clearSelectedTtlInState = () => {
    setState((prev) => clearSelectedTtl(prev));
  };

  const handleBackToTtlForm = () => {
    navigate('/');
    setResetSubroutes();
    //elimino la selectedTtl dallo state
    clearSelectedTtlInState();
  };

  const handleResetClick = () => {
    createCustomAlert(
      'ATTENZIONE',
      'Una volta salvato il template il reset della custom activity sarà definitivo e non sarà più possibile recuperare i dati precedenti',
      'resetta',
      () => {
        setState(prev => ({ ...prev, values: initialState }));
      },
      true,
      'Annulla'
    );
  };

  const buildLeftButtons = () => {
    const commonTestingAndReset = [
      {
        id: 'testing',
        label: 'TESTING',
        onClick: props.openTestingPopup,
        variant: 'anchor'
      },
      {
        id: 'reset',
        label: 'RESET',
        onClick: handleResetClick,
        variant: 'secondary'
      }
    ];

    switch (state.values.templateType) {
      case TEMPLATE_TYPE.TTL:
         if (currentPath === '/ttl/queueManager') {
          return [
            {
              id: 'back-banner-form',
              label: 'INDIETRO',
              onClick: handleBackToTtlForm,
              variant: 'secondary'
            }
          ];
        }
        // TTL: RECUPERA CAMPAGNA, TESTING, RESET
        return [
          {
            id: 'retrieve-campaign-ttl',
            label: 'RECUPERA CAMPAGNA',
            onClick: () => setRetrieveVersionModalIsVisible(true),
            variant: 'anchor'
          },
          ...commonTestingAndReset
        ];

      case TEMPLATE_TYPE.PUSH:
        // PUSH: TESTING, RESET
        return commonTestingAndReset;

      case TEMPLATE_TYPE.BANNER:
        // BANNER /banner/queueManager -> solo INDIETRO
        if (currentPath === '/banner/queueManager') {
          return [
            {
              id: 'back-banner-form',
              label: 'INDIETRO',
              onClick: handleBackToBannerForm,
              variant: 'secondary'
            }
          ];
        }

        // BANNER /banner/create (o default): RECUPERA CAMPAGNA, TESTING, RESET
        return [
          {
            id: 'retrieve-campaign-banner',
            label: 'RECUPERA CAMPAGNA',
            onClick: () => setRetrieveVersionModalIsVisible(true),
            variant: 'anchor'
          },
          ...commonTestingAndReset
        ];

      default:
        return commonTestingAndReset;
    }
  };

  // Bottoni DESTRA in base a templateType + currentPath
  const buildRightButtons = () => {
    const saveButton = {
      id: 'save',
      label: 'SALVA',
      onClick: () => api.saveTemplate(fromStateToRequest(state.values)),
      variant: 'primary',
      disabled: disableSaveButton(state.values)
    };

    switch (state.values.templateType) {
      case TEMPLATE_TYPE.BANNER:
        // BANNER /banner/queueManager -> nessun bottone a destra
        if (currentPath === '/banner/queueManager') {
          return [];
        }

        // BANNER /banner/create (o default): VISUALIZZA CODE + SALVA
        return [
          {
            id: 'queue',
            label: 'VISUALIZZA CODE',
            onClick: () => setBannerQueueModalIsVisible(true),
            variant: 'anchor'
          },
          saveButton
        ];
      case TEMPLATE_TYPE.TTL:
        // TTL /ttl/queueManager -> nessun bottone a destra
        if (currentPath === '/ttl/queueManager') {
          return [];
        }
        return [saveButton];
      case TEMPLATE_TYPE.PUSH:
      default:
        return [saveButton];
    }
  };

  const footerFactory = () => {
    if (
      ![
        TEMPLATE_TYPE.TTL,
        TEMPLATE_TYPE.PUSH,
        TEMPLATE_TYPE.BANNER
      ].includes(state.values.templateType)
    ) {
      return null;
    }

    const leftButtons = buildLeftButtons();
    const rightButtons = buildRightButtons();

    return (
      <Footer
        leftButtons={leftButtons}
        rightButtons={rightButtons}
      />
    );
  };

  const getBannerRoute = () => {
    switch (currentPath) {
      case '/banner/create':
        return {
          routes:
            <PanelBanner
              appConfig={props.appConfig}
              values={state.values}
              enums={hasInfo ? info : undefined}
              setValues={(field, value) => {
                setState(prev => ({ ...prev, values: { ...prev.values, [field]: value } }));
              }}
            />
        }
      case '/banner/queueManager':
        return {
          routes:
            <PanelBannerQueueManager
              api={api}
              appConfig={props.appConfig}
              statePromoBanner={state.slots}
              handleBackToBannerForm={handleBackToBannerForm}
            />
        }
      default:
        return {
          routes:
            <PanelBanner
              appConfig={props.appConfig}
              values={state.values}
              enums={hasInfo ? info : undefined}
              setValues={(field, value) => {
                setState(prev => ({ ...prev, values: { ...prev.values, [field]: value } }));
              }}
            />
          }
    }
  };

  const getBannerSubroutes = () => {
    switch (currentPath) {
      case '/banner/create':
        return [{
            id: 'previewBanner',
            label: 'Preview',
            component: <PreviewBannerWrapper values={state.values}/>
          }]
      case '/banner/queueManager':
        return [
            {
              id: 'previewBanner',
              label: 'Box1',
              component: <PreviewQueueBanner promobanner={state.slots.box1} />
            },
            {
              id: 'previewBannerBox2',
              label: 'Box2',
              component: <PreviewQueueBanner promobanner={state.slots.box2} />
            },
            {
              id: 'previewBannerBox3',
              label: 'Box3',
              component: <PreviewQueueBanner promobanner={state.slots.box3} />
            }
          ]
      default:
        return [{
          id: 'previewBanner',
          label: 'Preview',
          component: <PreviewBannerWrapper values={state.values}/>
        }]
    }
  }

  const buildTtlSubroutes = (values) => [
    {
      id: 'previewDesktop',
      label: 'Desktop',
      component: <PreviewDesktop values={values} />
    },
    {
      id: 'previewApp',
      label: 'App',
      component: <PreviewApp values={values} />
    },
    {
      id: 'previewNotifica',
      label: 'Tile Notification',
      component: <PreviewNotifica values={values} />
    }
  ];

  const handleViewTtlFromQueue = (ttl) => {
    setState((prev) => ({
      ...prev,
      slots: {
        ...prev.slots,
        selectedTtl: ttl
      }
    }));
  };

  const getTtlRoute = () => {
    switch (currentPath) {
      case '/ttl/queueManager':
        const selectedTtl = state.slots?.selectedTtl;
        const previewValues = selectedTtl?.templateInfo || {};
        return {
          routes: (
            <PanelTtlQueueManager
              api = {api}
              appConfig={props.appConfig}
              slots={state.slots || {}}
              isLoading={props.loading}
              triggerTimelineTemplates={info.triggerTimelineTemplates || []}
              onViewTtl={handleViewTtlFromQueue}
              handleBackToTtlForm={handleBackToTtlForm}
              clearSelectedTtlInState={clearSelectedTtlInState}
            />
          ),
          subroutes: buildTtlSubroutes(previewValues)
        };

      default:
        return {
          routes: (
            <Panel
              appConfig={props.appConfig}
              values={state?.values}
              enums={hasInfo ? info : undefined}
              setValues={(field, value) => {
                setState((prev) => ({
                  ...prev,
                  values: { ...prev.values, [field]: value }
                }));
              }}
            />
          ),
          subroutes: buildTtlSubroutes(state.values)
        };
    }
  };

  const ttlRoute = getTtlRoute();
  const bannerRoute= getBannerRoute();
  const bannerSubroutes = getBannerSubroutes();

  return (
    <>
      <main>
        {!props.loading ? (
          <SplitPanelRouter
            ref={splitPanelRouterRef}
            routes={[
              {
                id: 'panel',
                label: 'Desktop/App',
                disabled: !state?.values?.newTemplate && (state?.values?.templateType === TEMPLATE_TYPE.PUSH || state?.values?.templateType === TEMPLATE_TYPE.BANNER) || currentPath === '/banner/queueManager',
                component: ttlRoute.routes,
                onChange: (setTab) => {
                  if (handleChangeTab(TEMPLATE_TYPE.TTL)){
                    setTab();
                    navigate('/');
                  }
                },
                subroutes: ttlRoute.subroutes
              },
              {
                id: 'panelPush',
                label: 'Push notification',
                disabled: !state?.values?.newTemplate &&
                          state?.values?.templateType &&
                          (state?.values?.templateType.includes('TTL') || state?.values?.templateType.includes('BANNER')) ||
                          currentPath === '/banner/queueManager' ||
                          currentPath === '/ttl/queueManager',
                component: (
                  <PanelPush
                    appConfig={props.appConfig}
                      values={state?.values}
                      enums={hasInfo ? info : undefined}
                      setValues={(field, value) => {
                        setState(prev => ({ ...prev, values: { ...prev.values, [field]: value } }));
                      }}
                    />
                ),
                onChange: (setTab) => {
                  if (handleChangeTab(TEMPLATE_TYPE.PUSH)){
                    setTab();
                    navigate('/');
                  }
                },
                subroutes: [
                  {
                    id: 'previewPush',
                    label: 'Push',
                    component: <PreviewPush value={state.values} />
                  },
                  {
                    id: 'previewPushEpanded',
                    label: 'Push Expanded',
                    component: <PreviewPushExpanded value={state.values} />
                  }
                ]
              },
              {
                id: 'panelBanner',
                label: 'Promo Banner',
                disabled:
                  (!state?.values?.newTemplate &&
                    state?.values?.templateType &&
                    (state?.values?.templateType.includes(TEMPLATE_TYPE.TTL) ||
                      state?.values?.templateType.includes(TEMPLATE_TYPE.PUSH))) ||
                  currentPath === '/ttl/queueManager',
                component: (
                  bannerRoute.routes
                ),
                onChange: (setTab) => {
                  if (handleChangeTab(TEMPLATE_TYPE.BANNER)) {
                    setTab()
                    navigate('/banner/create');
                  }
                  ;
                },
                subroutes: bannerSubroutes
              }
            ]}
            mainActiveRoute={selectedMainActiveRoute}
            subActiveRoute={selectedSubActiveRoute}
      />
        ) : <Spinner />}
        {footerFactory()}
        {
          bannerQueueModalIsVisible ?
            <BannerQueueModal
              loading={props.loading}
              appConfig={props.appConfig}
              api={api}
              bannerQueueModalIsVisible = { bannerQueueModalIsVisible }
              setBannerQueueModalIsVisible = { setBannerQueueModalIsVisible }
            /> :
            <></>
        }
        {
          retrieveVersionModalIsVisible ?
            <RetrieveVersionModal
              loading={props.loading}
              visible={retrieveVersionModalIsVisible}
              templateType={templateType}
              loadVersions={retrieveVersionProps.fetchCampaignVersions}
              onVersionChange={retrieveVersionProps.onVersionChange}
              onConfirm={retrieveVersionProps.onConfirm}
              onClose={handleCloseRetrieveVersionModal}
              title="Scegli la versione del tuo journey"
              versionsLabel="Lista versioni"
              placeholder="Seleziona un ID"
            /> :
            <></>
        }
      </main>
    </>
  );
};
export default Main;

